import { universalFields, mediaRadiusField } from '../_shared/universal.js';
import { mediaGalleryBlockSchema } from '../../blocks/media-gallery/index.js';
import { buyBoxBlockSchemas, detailsBlockSchema } from '../_shared/buyBoxBlocks.js';
/**
 * Gallery — Shrine's `main-product_gallery` STATIC child: the media column wrapper. Holds the
 * media-gallery block (+ later: badges/trust over the media). No own settings.
 */
const galleryBlockSchema = {
    type: 'gallery',
    name: 'Gallery',
    fields: [],
    accepts: ['media-gallery'],
};
/**
 * main-product — Shrine's product section: a `.product` two-column layout (gallery LEFT, info RIGHT,
 * sticky on desktop) holding TWO STATIC children — Gallery + Details — each a CONTAINER that renders its
 * own blocks via the recursive BlockRenderer. Every buy-box piece is a reusable registry block. The
 * section settings cover the Shrine layout knobs (media size/position, sticky) + the universal band.
 */
export const mainProductSchema = {
    type: 'main-product',
    name: 'Product information',
    version: 2,
    templates: ['product'],
    fields: [
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            group: 'General',
            info: 'Adds an anchor id to this section so a link can scroll to it.',
            default: false,
        },
        {
            id: 'mediaSize',
            type: 'select',
            label: 'Media size',
            group: 'Product layout',
            default: 'medium',
            options: [
                { value: 'small', label: 'Small (45%)' },
                { value: 'medium', label: 'Medium (55%)' },
                { value: 'large', label: 'Large (65%)' },
            ],
        },
        {
            id: 'mediaPosition',
            type: 'select',
            label: 'Media position',
            group: 'Product layout',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'enableStickyInfo',
            type: 'checkbox',
            label: 'Sticky info on desktop',
            group: 'Product layout',
            info: 'The product-info column stays in view as the gallery scrolls (desktop only).',
            default: true,
        },
        mediaRadiusField,
        ...universalFields,
    ],
    // Static children (gallery/details) + every buy-box block type they can host (so the recursive tree
    // instantiates + the future builder validates drops).
    accepts: ['gallery', 'details'],
    staticChildren: [
        { type: 'gallery', label: 'Gallery' },
        { type: 'details', label: 'Details' },
    ],
    blocks: [
        galleryBlockSchema,
        detailsBlockSchema,
        mediaGalleryBlockSchema,
        // The buy-box pieces themselves, shared verbatim with the standalone `buy-box` section so the two can
        // never offer different blocks (see sections/_shared/buyBoxBlocks).
        ...buyBoxBlockSchemas,
    ],
    maxBlocks: 40,
};
/**
 * Default product information = Shrine's default tree, made a working COD + card buy-box (feature 0026 C2 S5):
 *   Gallery [static] → media-gallery
 *   Details [static] → title · rating · price · variant-picker · quantity · cod-form · description · collapsible-row
 * The cod-form is the SELF-CONTAINED order block — it collects the buyer's coordinates AND places the order
 * (COD, or Card when enabled) via its own "Commander" button — so the default leads with it and omits the
 * separate buy-buttons block (kept registered + available, just not in the default funnel).
 */
export const mainProductPresets = [
    {
        name: 'Product information',
        settings: {
            mediaSize: 'medium',
            mediaPosition: 'left',
            enableStickyInfo: true,
            colorScheme: 'light',
            paddingTop: 36,
            paddingBottom: 36,
            width: 'wide',
        },
        blocks: [
            {
                type: 'gallery',
                static: true,
                blocks: [
                    {
                        type: 'media-gallery',
                        // Pin the Shrine above-the-fold look: thumbnails BELOW the main image + NO desktop overlay
                        // arrows (Shrine `desktop_arrows_position` defaults to "hidden" — desktop navigation is via
                        // the thumbnail strip, and clicking the main image opens the fullscreen lightbox). `contain`
                        // fit shows images un-cropped. The full Shrine config surface stays available for editing.
                        // Mobile gallery (founder's chosen default): a swipeable carousel with the thumbnail strip BELOW
                        // it (so every image is visible on phones) and NO overlay arrows (mobile_arrows_position hidden)
                        // — hiding the arrows removes the white-circle controls entirely. `transparentArrows` stays true
                        // so IF arrows are ever turned on they render as bare chevrons, never a white circle. Desktop:
                        // thumbnails below the main image, no overlay arrows, contain fit.
                        settings: {
                            mediaFit: 'contain',
                            aspectRatio: 'square',
                            galleryLayout: 'thumbnail_slider',
                            thumbnailPosition: 'bottom',
                            desktopArrowsPosition: 'hidden',
                            mobileArrowsPosition: 'hidden',
                            transparentArrows: true,
                            mobileThumbnailsPosition: 'bottom',
                        },
                    },
                ],
            },
            {
                type: 'details',
                static: true,
                blocks: [
                    { type: 'title' },
                    { type: 'rating', settings: { rating: 4.4, label: '(1024 avis)' } },
                    { type: 'price' },
                    { type: 'variant-picker', settings: { variantStyle: 'auto' } },
                    { type: 'quantity' },
                    // The SELF-CONTAINED inline COD order block: the buyer fills name/phone/city/address (+ note) AND
                    // orders in one block via its own "Commander" button (COD by default, Card when the store enables
                    // it), the Releasit/EasySell single-page funnel. It is the primary order block, so the default omits
                    // a separate buy-buttons block (which would show a second order button and split the funnel). A
                    // buy-buttons block added inside this buy-box is superseded by the cod-form and renders NOTHING there
                    // (see the `if (cs) return null;` guard in BuyButtonsBlock); it only renders in non-buy-box sections.
                    { type: 'cod-form', settings: { formTitle: '@t:cod.form_title' } },
                    { type: 'description' },
                    {
                        type: 'collapsible-row',
                        settings: {
                            heading: '@t:section.shipping_returns',
                            content: {
                                format: 'html',
                                html: '<p>Expédié sous 24–48h. Retours faciles sous 14 jours.</p>',
                            },
                        },
                    },
                ],
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const mainProductMeta = {
    label: 'Main product',
    group: 'Product',
    icon: 'layout',
};
//# sourceMappingURL=schema.js.map